import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";

// Redux
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";

// Services
import VehicleApi from "Services/Vehicle";
import DriverApi from "Services/Driver";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AssignDriverModal = (props) => {
  const { openDialog, handleClose, vehicleRow } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const t = intl.formatMessage;
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  

  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");

  const fetchDrivers = useCallback(() => {
    DriverApi.getDriverList({ page: 1, limit: "unlimited", search: "", corporate_id : authUser.id }).then(
      (res) => {
        if (res?.status===200) {
          setDrivers(res?.data?.data?.data || []);
        }
      },
      () => {}
    );
  }, [authUser.id]);

  useEffect(() => {
    if (openDialog) {
      fetchDrivers();
      // vehicleRow.raw.assignedDriver can be an object or string
      const currentAssigned = vehicleRow?.raw?.assignedDriver;
      if (currentAssigned) {
        setSelectedDriver(currentAssigned._id || currentAssigned);
      } else {
        setSelectedDriver("");
      }
    }
  }, [openDialog, vehicleRow, fetchDrivers]);

  const handleAssignSubmit = () => {
    dispatch(showLoader(t({ id: "vehicle.modal.assign.loading" })));

    const data = {
      vehicleId: vehicleRow.raw._id,
      driverId: selectedDriver,
    };

    VehicleApi.assignDriver(data)
      .then((res) => {
        if (res?.status === 200) {
          const assignmentPayload = {
            driver_id: selectedDriver,
            vehicle_id: vehicleRow.raw._id,
            assigned_at: new Date().toISOString(),
          };

          VehicleApi.createDriverVehicleAssignment(assignmentPayload)
            .then(() => {
              dispatch(hideLoader());
              dispatch(
                showSnackbar({
                  message: res.data.message || t({ id: "vehicle.modal.assign.success" }),
                  autoHideDuration: 3000,
                  anchorOrigin: { vertical: "top", horizontal: "right" },
                  variant: "success",
                })
              );
              handleClose();
            })
            .catch((assignmentErr) => {
              dispatch(hideLoader());
              const message =
                assignmentErr?.response?.data?.message ||
                "Vehicle assigned, but failed to create assignment history";
              dispatch(
                showSnackbar({
                  message,
                  autoHideDuration: 3000,
                  anchorOrigin: { vertical: "top", horizontal: "right" },
                  variant: "error",
                })
              );
            });
        } else {
          dispatch(hideLoader());
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        const message = err?.response?.data?.message || t({ id: "vehicle.modal.assign.error" });
        dispatch(
          showSnackbar({
            message,
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      });
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="assign-driver-dialog"
      className={classes.root}
    >
      <DialogTitle>
        <section className={classes.dialogTitleContainer}>
          <Typography variant="body1" className={classes.title}>
            {t({ id: "vehicle.modal.assign.title" })}
          </Typography>
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </section>
      </DialogTitle>
      
      <DialogContent>
        {vehicleRow && (
          <div className={classes.vehicleInfoContainer}>
            {vehicleRow.vehicle_image ? (
              <img
                src={vehicleRow.vehicle_image}
                alt="Vehicle"
                className={classes.vehicleImage}
              />
            ) : (
              <div className={classes.noImageWrapper}>
                <DirectionsCarIcon style={{ fontSize: 56, color: "#aaa" }} />
              </div>
            )}
            <Typography className={classes.vehicleNo}>
              {vehicleRow.vehicle_no}
            </Typography>
          </div>
        )}

        <div className={classes.textFieldWrapper}>
          <TextField
            id="driver-select"
            label={t({ id: "vehicle.modal.assign.selectDriver" })}
            value={selectedDriver}
            onChange={(e) => setSelectedDriver(e.target.value)}
            select
            fullWidth
            variant="outlined"
            size="small"
          >
            <MenuItem value="">{t({ id: "vehicle.modal.assign.noDriver" })}</MenuItem>
            {drivers.map((d) => (
              <MenuItem key={d._id} value={d._id}>
                {d.firstName} {d.lastName} ({d.countryCode} {d.mobileNumber})
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className={classes.sbtBtnWrapper}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAssignSubmit}
            startIcon={<PersonAddAlt1Icon />}
            fullWidth
          >
            {selectedDriver ? t({ id: "vehicle.modal.assign.submit" }) : t({ id: "vehicle.modal.assign.removeSubmit" })}
          </Button>
        </div>
      </DialogContent>

      <Divider />
      <DialogActions>
        <section className={classes.dialogActions}>
          <Button onClick={handleClose}>{t({ id: "vehicle.modal.close" })}</Button>
        </section>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDriverModal;
